import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import FormInput from '@/shared/components/forms/FormInput';

interface IResendActivationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (data: { email: string; admin_name: string }) => Promise<void>;
  defaultEmail: string;
  defaultAdminName: string;
  isSending: boolean;
}

interface IResendActivationForm {
  email: string;
  admin_name: string;
}

const ResendActivationModal = ({
  isOpen,
  onClose,
  onSend,
  defaultEmail,
  defaultAdminName,
  isSending,
}: IResendActivationModalProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IResendActivationForm>({
    defaultValues: { email: defaultEmail, admin_name: defaultAdminName },
  });

  useEffect(() => {
    if (isOpen) {
      reset({ email: defaultEmail, admin_name: defaultAdminName });
    }
  }, [isOpen, defaultEmail, defaultAdminName, reset]);

  if (!isOpen) {
    return null;
  }

  const onValid = async (data: IResendActivationForm) => {
    await onSend(data);
  };

  const handleClose = () => {
    if (isSending) {
      return;
    }
    reset();
    onClose();
  };

  return (
    <div
      className='fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5'
      onClick={handleClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          handleClose();
        }
      }}
      role='button'
      tabIndex={0}
      aria-label='Close modal'
    >
      <div
        className='bg-white rounded-lg w-full max-w-md shadow-xl'
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role='dialog'
        tabIndex={-1}
      >
        <div className='flex justify-between items-center p-6 border-b border-gray-200 gap-4'>
          <h2 className='text-xl font-semibold text-gray-900 m-0'>Send Activation Email</h2>
          <button
            type='button'
            className='bg-transparent border-none cursor-pointer text-gray-500 p-1 flex items-center justify-center rounded shrink-0 hover:text-gray-900 hover:bg-gray-100'
            onClick={handleClose}
            disabled={isSending}
            aria-label='Close'
          >
            <svg
              width='20'
              height='20'
              viewBox='0 0 20 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M15 5L5 15M5 5L15 15'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit(onValid)} className='p-6'>
          <FormInput
            name='email'
            type='email'
            label='Organization Email'
            labelClassName='text-sm font-medium text-gray-700 mb-2'
            placeholder='org@example.com'
            register={register}
            errors={errors}
            validation={{
              required: 'Email is required',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Please enter a valid email address',
              },
            }}
            containerClassName='mb-4'
            className='!rounded-md !py-2 !text-sm border-gray-300'
          />

          <FormInput
            name='admin_name'
            label='Admin Name'
            labelClassName='text-sm font-medium text-gray-700 mb-2'
            placeholder='Enter admin name'
            register={register}
            errors={errors}
            validation={{ required: 'Admin name is required' }}
            containerClassName='mb-6'
            className='!rounded-md !py-2 !text-sm border-gray-300'
          />

          <div className='flex justify-end gap-3 pt-4 border-t border-gray-200'>
            <button
              type='button'
              className='px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 cursor-pointer disabled:opacity-50'
              onClick={handleClose}
              disabled={isSending}
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={isSending}
              className='px-4 py-2 bg-theme-500 text-white rounded-md text-sm font-semibold hover:bg-theme-600 cursor-pointer border-none disabled:opacity-50'
            >
              {isSending ? 'Sending...' : 'Send Activation Email'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResendActivationModal;
